export const CHECKOUT_OUTCOME = {
  CONFIRMED: 'CONFIRMED',
  CHECKING: 'CHECKING',
  SAFE_FAILURE: 'SAFE_FAILURE',
  UNKNOWN: 'UNKNOWN',
}

const LEGACY_UNCERTAIN_MESSAGES = [
  'processing your first order',
  'please contact the support. try again later',
]

const firstPresent = (source, fields) => {
  for (let index = 0; index < fields.length; index += 1) {
    if (source && source[fields[index]] !== undefined && source[fields[index]] !== null) {
      return source[fields[index]]
    }
  }
  return undefined
}

const normaliseOutcome = value => {
  const outcome = String(value || '').toUpperCase().replace(/[- ]/g, '_')
  if (['CONFIRMED', 'ORDER_CONFIRMED', 'PLACED', 'SUCCESS'].indexOf(outcome) !== -1) {
    return CHECKOUT_OUTCOME.CONFIRMED
  }
  if (['CHECKING', 'PROCESSING', 'PENDING', 'ORDER_CHECKING'].indexOf(outcome) !== -1) {
    return CHECKOUT_OUTCOME.CHECKING
  }
  if (['SAFE_FAILURE', 'KNOWN_SAFE_FAILURE', 'NOT_PLACED', 'ORDER_NOT_PLACED', 'FAILED'].indexOf(outcome) !== -1) {
    return CHECKOUT_OUTCOME.SAFE_FAILURE
  }
  return CHECKOUT_OUTCOME.UNKNOWN
}

const customerCopy = (outcome, orderNumber, postOrderFailure, retrySafe, supportReference) => {
  const orderLabel = orderNumber ? `Order ${orderNumber}` : 'Your order'

  switch (outcome) {
    case CHECKOUT_OUTCOME.CONFIRMED:
      return {
        title: 'Your order was placed',
        message: postOrderFailure
          ? `${orderLabel} was placed, but we could not complete a final checkout step.`
          : `${orderLabel} was placed, but we could not confirm every final checkout step.`,
        guidance: supportReference
          ? 'Please do not place this order again. Support can use the reference below to help.'
          : 'Please do not place this order again. Contact support for help.',
      }
    case CHECKOUT_OUTCOME.CHECKING:
      return {
        title: 'We are checking your order',
        message: 'We are still checking whether your order was placed.',
        guidance: 'Please do not place this order again while it is being checked.',
      }
    case CHECKOUT_OUTCOME.SAFE_FAILURE:
      return {
        title: 'Your order was not placed',
        message: 'We could not place your order.',
        guidance: retrySafe === true
          ? 'No order was created, so you can try again.'
          : supportReference
            ? 'Please contact support with the reference below before trying again.'
            : 'Please contact support before trying again.',
      }
    default:
      return {
        title: 'We could not confirm your order',
        message: 'We could not confirm whether your order was placed.',
        guidance: 'Please check My Orders or contact support before trying again. Do not place this order again until you have checked.',
      }
  }
}

export const mapCheckoutError = (error, context = {}) => {
  const response = error && error.response
  const backend = firstPresent(error, ['data', 'body', 'error']) ||
    (response && firstPresent(response, ['data', 'body'])) || error || {}
  const backendObject = typeof backend === 'object'
    ? backend
    : error && typeof error === 'object'
      ? error
      : {}
  const rawMessage = firstPresent(backendObject, ['message', 'errorMessage', 'description']) ||
    (typeof backend === 'string' ? backend : '') ||
    (error && error.message) || (typeof error === 'string' ? error : '')
  const status = firstPresent(backendObject, ['status', 'httpStatus']) ||
    (response && response.status) || context.status
  const responseHeaderReference = response && response.headers && response.headers.get
    ? response.headers.get('X-Support-Reference')
    : undefined
  const supportReference = firstPresent(backendObject, [
    'supportReference',
    'support_reference',
  ]) || responseHeaderReference || context.supportReference
  const orderNumber = firstPresent(backendObject, [
    'orderNumber',
    'publicOrderNumber',
    'order_number',
  ]) || context.orderNumber
  const declaredOutcome = firstPresent(backendObject, [
    'outcome',
    'orderOutcome',
    'failureCategory',
    'category',
  ])
  const retrySafe = firstPresent(backendObject, ['retrySafe', 'retry_safe'])
  const errorCode = firstPresent(backendObject, ['code']) || (error && error.code)
  const messageIsLegacyUncertain = LEGACY_UNCERTAIN_MESSAGES.some(message =>
    String(rawMessage || '').toLowerCase().indexOf(message) !== -1
  )
  const isNetworkOrServerFailure = !error || error.name === 'TypeError' || status >= 500

  if (errorCode === 'AUTHENTICATION_REQUIRED' || status === 401 || status === 403) {
    return {
      title: 'Please sign in again',
      message: 'Your session has expired, so this order was not submitted.',
      guidance: 'Sign in again, review your cart, and then place the order.',
      outcome: CHECKOUT_OUTCOME.SAFE_FAILURE,
      retrySafe: false,
      code: 'AUTHENTICATION_REQUIRED',
      status,
      backendMessage: rawMessage,
    }
  }

  if (context.preSubmitValidation) {
    return {
      title: context.title || 'Please check your order',
      message: rawMessage || 'Please check your order details.',
      guidance: 'Update the details and try again.',
      outcome: CHECKOUT_OUTCOME.SAFE_FAILURE,
      retrySafe: true,
      status,
      backendMessage: rawMessage,
    }
  }

  if (context.paymentOutcomeUnknown) {
    return {
      title: 'We could not confirm your payment',
      message: 'We could not confirm whether the payment completed.',
      guidance: supportReference
        ? 'Please do not try the payment again yet. Contact support with the reference below.'
        : 'Please do not try the payment again yet. Contact support before trying again.',
      outcome: CHECKOUT_OUTCOME.UNKNOWN,
      retrySafe: false,
      supportReference,
      status,
      backendMessage: rawMessage,
    }
  }

  let outcome = context.outcome
    ? normaliseOutcome(context.outcome)
    : normaliseOutcome(declaredOutcome)

  if (context.orderNumber && context.postOrderFailure) {
    outcome = CHECKOUT_OUTCOME.CONFIRMED
  } else if (messageIsLegacyUncertain || isNetworkOrServerFailure) {
    outcome = CHECKOUT_OUTCOME.UNKNOWN
  }

  const copy = customerCopy(outcome, orderNumber, context.postOrderFailure, retrySafe, supportReference)
  return {
    ...copy,
    outcome,
    orderNumber,
    supportReference,
    status,
    failureCategory: firstPresent(backendObject, ['failureCategory', 'category']),
    code: errorCode,
    retrySafe,
    backendMessage: rawMessage,
  }
}



// WEBPACK FOOTER //
// ./src/utils/checkoutError.js